import React from 'react';
import PropTypes from 'prop-types';
import isNil from 'utils/isNil';
import { colorTypeHelper, useIsColorDark } from 'utils/colorUtility';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import styled from 'styled-components';

const FAQPanel = ({ header, children, ...otherProps }) => (
  <Accordion {...otherProps}>
    <AccordionSummary expandIcon={<div className="arrow" />}>
      <h3 className="font-heading-md">{header}</h3>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const ContentfulFAQAccordion = ({
  backgroundColor: background,
  border,
  heading,
  subhead,
  components,
  customHeading,
  ...otherProps
}) => {
  const backgroundColor = colorTypeHelper[background];
  return (
    <SectionFaq
      background={background}
      backgroundColor={`var(${backgroundColor})`}
      border={border}
      id="websiteFAQAccordion"
      {...otherProps}
    >
      <div className="section-faq--container">
        {customHeading || (heading && <p className="section-faq--title font-h1">{heading}</p>)}
        {subhead?.subhead && <p className="section-faq--subhead">{subhead.subhead}</p>}
        <div className="section-faq--faq-container">
          {!isNil(components) &&
            components.map((faqItem, index) => {
              return (
                !isNil(faqItem.question && faqItem.answer) && (
                  <FAQPanel className="mobile-view" header={faqItem.question.question} key={index}>
                    {faqItem.answer.hasOwnProperty('childMarkdownRemark') ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: faqItem.answer.childMarkdownRemark.html,
                        }}
                      />
                    ) : (
                      faqItem.answer.answer
                    )}
                  </FAQPanel>
                )
              );
            })}
        </div>
      </div>
    </SectionFaq>
  );
};

ContentfulFAQAccordion.defaultProps = {
  border: true,
};

ContentfulFAQAccordion.propTypes = {
  background: PropTypes.object,
  title: PropTypes.string,
  subhead: PropTypes.object,
  components: PropTypes.array,
};

export default ContentfulFAQAccordion;

const SectionFaq = styled.div`
  background: ${({ backgroundColor }) => backgroundColor};
  .section-faq--container {
    padding: 80px 28px;
    max-width: 840px;
    margin: 0 auto;
    .section-faq--title {
      text-align: center;
      color: ${({ background }) =>
        useIsColorDark(background) ? 'var(--white)' : 'var(--darkest)'};
    }
    .section-faq--subhead {
      font-size: var(--font-size-20);
      line-height: var(--line-height-150);
      text-align: center;
      color: var(--dark);
    }
    .section-faq--faq-container {
      margin-top: 47px;
    }
    .section-faq--view-more {
      font-size: var(--font-size-13);
      line-height: 18px;
      text-align: center;
      color: var(--grey-6);
      cursor: pointer;
      display: none;
    }
  }
  @media (max-width: 767px) {
    .section-faq--container {
      padding: 48px 28px;
      max-width: 524px;
      .section-faq--title {
        font-size: var(--font-size-28);
        letter-spacing: var(--letter-spacing--0-03);
      }
      .section-faq--subhead {
        font-size: var(--font-size-18);
        line-height: 28px;
        margin-top: 22px;
        padding: 0 10px;
      }
      .section-faq--subhead {
        font-size: var(--font-size-20);
        line-height: var(--line-height-150);
        text-align: center;
        color: var(--dark);
      }
      .section-faq--faq-container {
        margin-top: 40px;
      }
    }
  }
  @media (max-width: 575px) {
    .section-faq--container {
      padding: 48px 20px;
      max-width: 100%;
      .section-faq--view-more {
        display: block;
      }
    }
  }

  .MuiAccordion-root {
    border: ${(props) => (props.border ? '1px solid var(--grey-3)' : 'none')};
    box-sizing: border-box;
    background: ${(props) => props.accordionBgColor};
    margin-bottom: 24px;
    border-radius: 0;
    box-shadow: none;

    &:before {
      display: none;
    }

    &:first-child {
      border-top: ${(props) => (props.border ? '1px solid var(--grey-3)' : 'none')};
    }

    .MuiAccordionSummary-root {
      padding: 0 40px 0 0;

      .MuiAccordionSummary-content {
        outline: none;
        margin: 0;
        padding: 28px 78px 28px 32px;
        position: relative;

        h3 {
          color: ${(props) => {
            return props.background
              ? useIsColorDark(props.accordionBgColor) && 'var(--white)'
              : 'var(--darkest)';
          }};
          margin: 0;
        }
      }

      .MuiAccordionSummary-expandIconWrapper {
        .arrow {
          border-left: 7px solid transparent;
          border-right: 7px solid transparent;
          border-top: 8px solid var(--titan-blue-3);
        }
      }
    }

    .MuiAccordionDetails-root {
      padding: 13px 32px 32px;
      background: ${(props) => props.accordionBgColor};
    }

    @media (max-width: 767px) {
      .MuiAccordionSummary-root {
        padding: 0 16px 0 0;

        .MuiAccordionSummary-content {
          padding: 24px 61px 24px 24px;

          h3 {
            font-size: var(--font-size-18);
          }
        }
      }

      .MuiAccordionDetails-root {
        padding: 4px 24px 24px;

        p {
          font-size: var(--font-size-16);
        }
      }
    }
  }

  .section-faq--faq-container.show-all-faq {
    .section-faq--view-more {
      display: none;
    }
  }
`;
